if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.customTime = this.dataset.customTime; // New property for custom time
        this.loop = false;
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            const countdownData = this.dataset.dueDate;
            if (countdownData) {
              this.targetTime = new Date(countdownData).getTime();
            }
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.startTime = Date.now();
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);
        this.classList.remove('hidden');
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);

        // For custom time countdowns, restart the countdown
        if (this.duration === 'custom_time' || (this.customTime && this.customTime.trim() !== '')) {
          this.targetTime = this.getEvergreenDueDate().getTime();
          this.startTime = Date.now();
          if (this.targetTime > this.startTime) {
            this.beginCountdown();
          }
        } else if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = '00';
          }
        });
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        // Fallback: if customTime is set, use custom time logic regardless of duration
        if (this.customTime && this.customTime.trim() !== '') {
          return this.getCustomTimeDueDate();
        }

        switch (this.duration) {
          case 'custom_time':
            return this.getCustomTimeDueDate();
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
          default:
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }

      getCustomTimeDueDate() {
        const now = new Date();
        const dueDate = new Date(now);

        if (!this.customTime) {
          // Default to 4pm if no custom time is set
          dueDate.setHours(16, 0, 0, 0);
        } else {
          // Parse the custom time (format: "HH:MM" or "HH:MM AM/PM")
          const timeParts = this.customTime.trim().split(' ');
          let hour, minute = 0;

          if (timeParts.length === 1) {
            // 24-hour format (e.g., "16:00")
            const [hourStr, minuteStr] = timeParts[0].split(':');
            hour = parseInt(hourStr, 10);
            minute = minuteStr ? parseInt(minuteStr, 10) : 0;
          } else if (timeParts.length === 2) {
            // 12-hour format (e.g., "4:00 PM")
            const [timeStr, period] = timeParts;
            const [hourStr, minuteStr] = timeStr.split(':');
            hour = parseInt(hourStr, 10);
            minute = minuteStr ? parseInt(minuteStr, 10) : 0;

            if (period.toUpperCase() === 'PM' && hour !== 12) {
              hour += 12;
            } else if (period.toUpperCase() === 'AM' && hour === 12) {
              hour = 0;
            }
          }

          // Validate hour and minute
          if (isNaN(hour) || hour < 0 || hour > 23) {
            hour = 16; // Default to 4pm
          }
          if (isNaN(minute) || minute < 0 || minute > 59) {
            minute = 0;
          }

          dueDate.setHours(hour, minute, 0, 0);
        }

        // If the time has already passed today, set it for tomorrow
        if (dueDate.getTime() <= now.getTime()) {
          dueDate.setDate(dueDate.getDate() + 1);
        }

        return dueDate;
      }
    }
  );
}
